import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Breadcrumb from '../../common/Breadcrumb';
import StepIndicator from './components/StepIndicator';
import SimpleCaptcha from './components/SimpleCaptcha';
import ArticlePreview from './components/ArticlePreview';
import GenerationProgress from './components/GenerationProgress';
import FetchProgress from './components/FetchProgress';
import ArticleHistory from './components/ArticleHistory';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Article to Media Tool - Main Page
 * Users submit URLs to generate audio, social posts, and TLDR
 */
const ArticleToMediaMain = () => {
  const history = useHistory();
  const [url, setUrl] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(() => {
    // Check sessionStorage for previous verification
    return sessionStorage.getItem('articleToolCaptchaVerified') === 'true';
  });
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [toolAvailable, setToolAvailable] = useState(true);

  // Processing states
  const [step, setStep] = useState('input'); // input, captcha, fetching, preview, processing
  const [fetchedHtml, setFetchedHtml] = useState('');
  const [fetchMethod, setFetchMethod] = useState('simple');
  const [progress, setProgress] = useState({});
  const [fetchProgress, setFetchProgress] = useState({});
  const [extractedMetadata, setExtractedMetadata] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null); // Track credit fallback info

  // Refs for stream cleanup
  const abortControllerRef = useRef(null);
  const readerRef = useRef(null);

  // Cleanup streams on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
      }
    };
  }, []);

  // Check if tool is available on mount
  useEffect(() => {
    checkToolAvailability();
  }, []);

  const checkToolAvailability = async () => {
    try {
      const response = await fetch('/api/tools/article/check-credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!data.hasCredits && data.error) {
        if (data.error.includes('temporarily unavailable')) {
          setToolAvailable(false);
        }
      }
    } catch (err) {
      console.error('Error checking tool availability:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch (err) {
      setError('Please enter a valid URL');
      return;
    }

    // Check URL length
    if (url.length > 2000) {
      setError('URL is too long (max 2000 characters)');
      return;
    }

    // Show captcha if not verified
    if (!captchaVerified) {
      setShowCaptcha(true);
      setStep('captcha');
      return;
    }

    await processUrl();
  };

  const handleCaptchaSuccess = async () => {
    setCaptchaVerified(true);
    sessionStorage.setItem('articleToolCaptchaVerified', 'true');
    setShowCaptcha(false);
    await processUrl();
  };

  const processUrl = async () => {
    setLoading(true);
    setError('');
    setCreditInfo(null);

    try {
      // First check credits BEFORE doing anything else
      const creditResponse = await fetch('/api/tools/article/check-credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessCode: accessCode || undefined })
      });
      const creditData = await creditResponse.json();

      if (!creditData.hasCredits) {
        setError(creditData.error || 'Insufficient credits to process this article.');
        setLoading(false);
        return;
      }

      // Track if we're using fallback to public credits
      if (creditData.usedFallback) {
        setCreditInfo({
          usedFallback: true,
          privateCodeName: creditData.privateCodeName,
          message: `Your access code${creditData.privateCodeName ? ` (${creditData.privateCodeName})` : ''} doesn't have enough credits for today. We'll use free public credits to generate this article.`
        });
      }

      // Check if URL already exists
      const checkResponse = await fetch('/api/tools/article/check-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const checkData = await checkResponse.json();

      if (checkData.error) {
        setError(checkData.error);
        setLoading(false);
        return;
      }

      if (checkData.exists) {
        // Redirect to existing article
        history.push(`/tools/article-to-media/${checkData.article.slug}`);
        return;
      }

      // Switch to fetching step with progress display
      setStep('fetching');
      setFetchProgress({});
      setLoading(false);

      // Create AbortController for cancellation
      abortControllerRef.current = new AbortController();

      // Use streaming fetch for progress updates
      const response = await fetch('/api/tools/article/fetch-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
        signal: abortControllerRef.current.signal
      });

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      // Helper to process SSE lines
      let currentEvent = null;
      const processLines = (lines) => {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === 'progress') {
                setFetchProgress(prev => ({
                  ...prev,
                  [data.step]: { status: data.status, message: data.message }
                }));
              } else if (currentEvent === 'complete') {
                // Fetch complete - show preview
                setFetchedHtml(data.html);
                setFetchMethod(data.fetchMethod);
                setExtractedMetadata(data.extractedMetadata);
                setExtractionError(data.extractionError);
                setStep('preview');
                return true; // Signal to stop processing
              } else if (currentEvent === 'error') {
                setError(data.error);
                setStep('input');
                return true; // Signal to stop processing
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
            currentEvent = null;
          }
        }
        return false; // Continue processing
      };

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          if (processLines(lines)) {
            break; // Complete or error event received
          }

          if (done) {
            // Process any remaining buffer content
            if (buffer.trim()) {
              const finalLines = buffer.split('\n');
              processLines(finalLines);
            }
            break;
          }
        }
      } finally {
        // Clean up reader
        readerRef.current = null;
        reader.cancel().catch(() => {});
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Intentional abort, don't show error
      }
      setError('Failed to fetch article. Please try again.');
      setStep('input');
    }
  };

  const handleConfirm = async () => {
    setStep('processing');
    setError('');
    setProgress({});

    // Create AbortController for cancellation
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/api/tools/article/confirm-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          html: fetchedHtml,
          fetchMethod,
          accessCode: accessCode || undefined,
          extractedMetadata: extractedMetadata || undefined
        }),
        signal: abortControllerRef.current.signal
      });

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      // Helper to process SSE lines
      let currentEvent = null;
      const processLines = (lines) => {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === 'progress') {
                setProgress(prev => ({
                  ...prev,
                  [data.step]: { status: data.status, ...data }
                }));
              } else if (currentEvent === 'complete') {
                // Pass credit info to results page if there was a fallback
                const navigationState = data.creditInfo ? { creditInfo: data.creditInfo } : undefined;
                history.push(`/tools/article-to-media/${data.slug}`, navigationState);
                return true; // Signal to stop processing
              } else if (currentEvent === 'error') {
                // Check if this is a CAPTCHA error - reset verification and show CAPTCHA
                if (data.error && data.error.toLowerCase().includes('captcha')) {
                  setCaptchaVerified(false);
                  sessionStorage.removeItem('articleToolCaptchaVerified');
                  setStep('captcha');
                } else {
                  setError(data.error);
                  setStep('preview');
                }
                return true; // Signal to stop processing
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
            currentEvent = null;
          }
        }
        return false; // Continue processing
      };

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          if (processLines(lines)) {
            break; // Complete or error event received
          }

          if (done) {
            // Process any remaining buffer content
            if (buffer.trim()) {
              const finalLines = buffer.split('\n');
              processLines(finalLines);
            }
            break;
          }
        }
      } finally {
        // Clean up reader
        readerRef.current = null;
        reader.cancel().catch(() => {});
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Intentional abort, don't show error
      }
      // Check if this is a network/timeout error
      if (err.name === 'TypeError' || err.message.includes('network') || err.message.includes('Failed to fetch')) {
        setError(
          'Processing is taking longer than expected. Your article may still be generating. ' +
          'Check the "Recently Generated" section below or refresh the page in a minute to see your results.'
        );
        setStep('input');
        setUrl('');
      } else {
        setError('Failed to process article. Please try again.');
        setStep('preview');
      }
    }
  };

  const handleBackToInput = () => {
    setStep('input');
    setError('');
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Tools', path: '/tools' },
    {
      label: 'Article to Media',
      path: '/tools/article-to-media',
      onClick: step !== 'input' ? handleBackToInput : undefined
    }
  ];

  if (!toolAvailable) {
    return (
      <>
        <Helmet>
          <title>Article to Media | Elliot Koss</title>
        </Helmet>
        <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
          <Breadcrumb items={breadcrumbItems} />
          <Alert variant="warning">
            This tool is temporarily unavailable. Please check back later.
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Article to Media - Transform Articles | Elliot Koss</title>
        <meta name="description" content="Transform any article URL into audio narration, social media posts with AI-generated images, and a TLDR summary." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
        <Breadcrumb items={breadcrumbItems} />

        <StepIndicator currentStep={step === 'preview' ? 2 : step === 'processing' ? 3 : step === 'fetching' ? 1 : 1} />

        <Row className="mb-4">
          <Col lg={8}>
            {step === 'fetching' ? (
              <>
                <h1>Fetching Article</h1>
                <p className="text-muted">
                  Please wait while we fetch and extract your article.
                </p>
              </>
            ) : step === 'preview' ? (
              <>
                <h1>Preview Article</h1>
                <p className="text-muted">
                  Verify this is the article you want to process.
                </p>
              </>
            ) : step === 'processing' ? (
              <>
                <h1>Generating Media</h1>
                <p className="text-muted">
                  Please wait while we process your article.
                </p>
              </>
            ) : (
              <>
                <h1>Article to Media</h1>
                <p className="text-muted">
                  Transform any article URL into audio narration, social media posts with AI-generated images, and a TLDR summary.
                </p>
                <p className="text-muted mb-1" style={{ fontSize: '0.875rem' }}>
                  New publications are added to the allowlist as you submit them. See Recently Generated below for supported sites.
                </p>
                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                  <a href="/ai-notes/article-to-media-turn-one-link-into-tldr-audio-and-social" style={{ color: 'inherit' }}>
                    Read more about this tool
                  </a>
                </p>
              </>
            )}
          </Col>
        </Row>

        {step === 'fetching' ? (
          <FetchProgress progress={fetchProgress} creditInfo={creditInfo} />
        ) : step === 'processing' ? (
          <GenerationProgress progress={progress} creditInfo={creditInfo} />
        ) : step === 'preview' ? (
          <ArticlePreview
            htmlContent={fetchedHtml}
            url={url}
            onConfirm={handleConfirm}
            loading={loading}
            error={error}
            extractedMetadata={extractedMetadata}
            extractionError={extractionError}
            creditInfo={creditInfo}
          />
        ) : step === 'captcha' ? (
          <SimpleCaptcha
            onSuccess={handleCaptchaSuccess}
            onCancel={() => {
              setShowCaptcha(false);
              setStep('input');
            }}
          />
        ) : (
          <>
            <Row className="mb-5">
              <Col lg={8}>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Article URL</Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://example.com/article"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      disabled={loading}
                      size="lg"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Access Code (optional)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter access code if you have one"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      disabled={loading}
                    />
                  </Form.Group>

                  {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={loading || !url.trim()}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Processing...
                      </>
                    ) : (
                      'Generate Media'
                    )}
                  </Button>

                  <p className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                    By using this tool, you agree to my{' '}
                    <Link to="/terms-of-service">Terms of Service</Link> and{' '}
                    <Link to="/privacy-policy">Privacy Policy</Link>
                  </p>
                </Form>
              </Col>
            </Row>

            <ArticleHistory />
          </>
        )}
      </Container>
    </>
  );
};

// Wrap with ErrorBoundary for graceful error handling
const ArticleToMediaMainWithErrorBoundary = () => (
  <ErrorBoundary>
    <ArticleToMediaMain />
  </ErrorBoundary>
);

export default ArticleToMediaMainWithErrorBoundary;
